import useTransactionStore from "../stores/useTransactionStore";

/**
 * cartIdentityGuard — stale cart identity (sOrderID) protection.
 *
 * A reload can restore a pre-checkout cart snapshot whose sOrderID was already
 * consumed by a completed order: useCartStore persists through sqliteStorage,
 * which debounces writes by 100ms, so the post-checkout ID reset may never be
 * flushed before a refresh/crash/PWA update. Reusing a consumed sOrderID is
 * destructive twice over:
 *   1. db.orders is keyed on sOrderID, so the new sale silently overwrites the
 *      earlier order locally.
 *   2. The backend dedupes on sOrderID — it returns "200" without creating a
 *      record, the POS marks the order isSynced=1, and the bill is lost
 *      server-side while appearing synced locally.
 * (Production incident: HSN/103148 and HSN/103176 shared one sOrderID.)
 *
 * A resurrected stale snapshot can carry BOTH a matching documentno and a
 * matching sOrderID (RTN/100614→100618: the reload restored the whole cart,
 * not just one field), which defeats a documentno-only comparison — the
 * incoming sale looked like a legitimate retry even though its content (total,
 * item count) was entirely different. shouldRegenerateIdentityAtSave verifies
 * content matches before trusting a documentno match for that reason.
 *
 * Two decision helpers, one per defense layer. ID regeneration itself stays at
 * the call sites so each layer controls exactly which IDs are safe to reissue.
 */

/**
 * True when a documentno belongs to a parked series — parked bills ("PB/") or
 * pre-orders/advance orders (till-configured prefix, e.g. "ADV/"). A retrieved
 * cart legitimately shares its sOrderID with a db.orders row ONLY while that
 * row still carries its parked-series number; once the row holds a regular
 * bill number the parked bill was already completed, and any cart still
 * claiming its identity is a stale snapshot.
 */
export const isParkedSeriesDocumentno = (documentno, preorderPrefix) => {
  if (!documentno) return false;
  if (documentno.startsWith("PB/")) return true;
  if (preorderPrefix && documentno.startsWith(preorderPrefix)) return true;
  return false;
};

/**
 * Save-time (DI-33) check — used in processOrderApproved after documentno is
 * resolved and before any DB write, and in syncOrders' fresh-order branch
 * before the online POST. Returns true when the order about to be saved/posted
 * would overwrite a DIFFERENT existing order — i.e. same sOrderID but a
 * different documentno, and not a flow that legitimately reuses its identity.
 *
 * Same-documentno matches are NOT stale: that is the same order being saved
 * again (e.g. a checkout retry after a partial failure) and regenerating there
 * would duplicate the sale.
 */
export const shouldRegenerateIdentityAtSave = ({ existingOrder, cart, documentno, preorderPrefix }) => {
  if (!existingOrder) return false;
  if (existingOrder.documentno === documentno) {
    // Same documentno normally proves "this exact order being re-saved" (e.g. a
    // checkout retry after a partial failure) — legitimate, no regeneration needed.
    // But a reload/crash can resurrect a stale pre-checkout cart snapshot whose
    // documentno AND sOrderID both revert together (RTN/100614→100618 incident:
    // both identifiers matched, yet the sale was a different customer/₹595/4 items,
    // not a retry of the ₹510/2-item sale already saved under that identity).
    // A genuine retry re-submits identical content, so require total + item count
    // to match before trusting the documentno match; otherwise fall through to the
    // same flow-specific exemptions below (Layaway/split/OCU/credit/parked-retrieve
    // legitimately reuse an identity with changing content and stay exempt).
    // `|| 0` normalizes a missing/undefined total on either side before comparing —
    // without it, Number(undefined) === Number(undefined) is NaN === NaN, which is
    // always false, so two carts that both simply lack a .total (e.g. a minimal/
    // synthetic cart) would incorrectly fail this match and fall through to the
    // unconditional `return true` below, misclassifying a legitimate same-order
    // retry as a stale identity.
    const sameTotal = Number(cart?.total || 0) === Number(existingOrder.total || 0);
    const sameItemCount = (cart?.items?.length || 0) === (existingOrder.items?.length || 0);
    if (sameTotal && sameItemCount) return false;
    // TSK-2935: a Prepare-Bill-confirmed order's sOrderID is the only legitimate
    // identity for the rest of its lifecycle — completing it (discount may have
    // changed the total) or cancelling it (total zeroes out) both legitimately
    // change total/item count from what Prepare Bill originally saved, so the
    // content-match check above doesn't apply here. documentno already matching
    // (this if-block) plus preparedBillOrigin confirms this is that same order,
    // not an unrelated stale snapshot. Confirmed via production trace: cancelling
    // a Prepare-Bill order (BHRS/1781) tripped this exact false positive and
    // orphaned its confirmed sOrderID mid-cancel.
    if (existingOrder.preparedBillOrigin === "Y") return false;
  }
  if (cart?.type === "Layaway" || cart?.layAway === "Y" || cart?.docType === "Advance") {
    // Layaway close/re-save legitimately reuses this sOrderID only while the
    // existing row is still the open parked-series (advance) record. A cart
    // that never flushed its post-checkout reset can still carry a stale
    // type: "Layaway"/layAway: "Y"/docType: "Advance" flag after this identity
    // was already closed under a different documentno — trusting the flag
    // alone let a later, unrelated save silently overwrite a completed sale
    // (production: S1S/39378 -> CS1S CAN/4, 2026-07-28 — this exact sOrderID
    // had closed 3 minutes earlier). Require the row to still be parked
    // before exempting.
    //
    // BUG-1914: SuperMarket/Retail advance orders only ever set cart.layAway
    // ("Y"/"N") — that module never sets cart.type. Checking type alone left
    // every SuperMarket advance-order completion falling through to the
    // unconditional `return true` at the bottom of this function, minting a
    // fresh sOrderID the backend has no matching s_order row for (confirmed:
    // RAK2 ADV/14, sOrderID CB949A26... -> completion posted as a new
    // sOrderID 9DBD8DB2..., stuck in Pending Synchronization). isStalePersistedCart
    // below already ORs both flags for the same reason; this brings the
    // save-time check in line with it.
    //
    // docType: "Advance" is included too — usePaymentFlow.js and
    // useSuperMarketActions.js already treat it as an independent alternative
    // to layAway ("isAdvanceOrder = layAway==='Y' || docType==='Advance'"),
    // so a cart can reach this guard with only docType set. Safe to fold into
    // this branch: the isParkedSeriesDocumentno gate below still requires the
    // existing row to be genuinely open before exempting, so a stale/leftover
    // docType flag (useOrderProcessing.js's own comment above ~line 1616 notes
    // it can be a leftover from an abandoned retrieval) still can't resurrect
    // an already-closed order — same protection layAway/type already get.
    return !isParkedSeriesDocumentno(existingOrder.documentno, preorderPrefix);
  }
  if (cart?.isSplit === "Y" || cart?.statusType === "OCU" || cart?.isCreditSale === "Y") {
    // Unlike Layaway (which deliberately transitions from a parked-series number
    // to a new regular one), these flows resolve to the SAME documentno on every
    // save throughout their lifecycle — a non-PB/ADV cart just keeps cart.documentno
    // as-is (see resolvedDocumentNo above, ~line 495). So a genuine incremental
    // re-save of the SAME order always matches existingOrder.documentno exactly;
    // if it doesn't, this identity was already consumed by a different, completed
    // order and the flag alone must not be trusted blindly (same root cause as
    // the Layaway gap above — a stale isSplit/OCU/isCreditSale flag surviving on
    // an unrelated new cart would otherwise be exempted just as silently).
    return !!existingOrder.documentno && existingOrder.documentno !== documentno;
  }
  if (cart?.isRetrived === "Y") {
    // Retrieved PB/ADV bill completing under a new regular number — legitimate
    // reuse, but only while the existing row is still the parked one. If the
    // row already carries a regular bill number, that completion already
    // happened and this cart is a stale snapshot of it.
    return !isParkedSeriesDocumentno(existingOrder.documentno, preorderPrefix);
  }
  return true;
};

/**
 * Mount-time (BUG-1477) check — used in useCoreEffects after fetching the
 * db.orders row matching the persisted cart's sOrderID. Returns true when the
 * persisted cart is a stale snapshot of an already-saved order and must be
 * cleared. Layaway and confirmed-restaurant carts intentionally share their
 * sOrderID with a db.orders row; retrieved carts do too, but only while that
 * row still carries its parked-series documentno (see isParkedSeriesDocumentno).
 */
export const isStalePersistedCart = ({ cart, existingOrder, preorderPrefix }) => {
  if (!existingOrder) return false;
  if (cart?.layAway === "Y" || cart?.type === "Layaway") {
    // Same identity-trust gap as shouldRegenerateIdentityAtSave above: a
    // Layaway cart is only legitimately unfinished while its row is still
    // the parked (advance) record. If the row already shows a different,
    // already-closed documentno, this persisted cart is stale and must not
    // survive reload to potentially overwrite that closed sale later.
    return !isParkedSeriesDocumentno(existingOrder.documentno, preorderPrefix);
  }
  if (cart?.statusType === "OCU") {
    // Same reasoning as shouldRegenerateIdentityAtSave's isSplit/OCU/isCreditSale
    // branch: this flow's documentno never changes across its lifecycle, so a
    // persisted cart genuinely continuing the same order always still matches the
    // row's current documentno. A mismatch means the row moved on since this cart
    // was last flushed to disk — stale, must not survive reload.
    return !!existingOrder.documentno && existingOrder.documentno !== cart?.documentno;
  }
  if (cart?.isRetrived === "Y") {
    return !isParkedSeriesDocumentno(existingOrder.documentno, preorderPrefix);
  }
  return true;
};

/**
 * DB-layer backstop (last line of defense) — called from both DexieAdapter and
 * SQLiteAdapter's Table.put() for the "orders" table only. Every known local
 * write path (checkout, cancel/void, discard-parked-bill) already calls
 * shouldRegenerateIdentityAtSave at its own call site before saving; this exists
 * so a FUTURE call site that forgets that check still can't silently overwrite a
 * completed sale — the destructive write simply cannot reach the table.
 *
 * Confirmed necessary in production: VRP/101112, a real ₹326.40 sale, was
 * overwritten by a ₹0 CAN/319 cancel record sharing its sOrderID (clearCart had
 * no identity check at all before this was added).
 *
 * Exempt when data.isSynced is 1/"1" — that is the existing convention (see
 * useOrderProcessing.js's `if (!isSynced)` guard, exportSequenceDiagnostics'
 * unsyncedOrders filter) for "this write is a trusted, server-confirmed record,"
 * e.g. useWebSocketHandler.js's online-order upsert, which legitimately
 * re-writes the same sOrderID on every status update and must not be touched.
 *
 * @param {Object} data - the row about to be written (mutated in place if regenerated)
 * @param {(sOrderID: string) => Promise<Object|undefined>} getExistingOrder
 * @param {(entry: Object) => void} [logAuditEntry] - best-effort, non-blocking
 */
export async function guardOrderIdentityBeforeWrite(data, getExistingOrder, logAuditEntry) {
  if (!data || !data.sOrderID) return;
  if (data.isSynced === 1 || data.isSynced === "1") return;
  try {
    const existingOrder = await getExistingOrder(data.sOrderID);
    if (!existingOrder) return;

    let preorderPrefix = null;
    try {
      const { default: useSessionStore } = await import("../stores/useSessionStore");
      preorderPrefix = useSessionStore.getState().preorderSequence?.prefix;
    } catch {
      // best-effort only — absence just means the parked/pre-order exemption is skipped this time
    }

    if (!shouldRegenerateIdentityAtSave({ existingOrder, cart: data, documentno: data.documentno, preorderPrefix })) return;

    const staleSOrderID = data.sOrderID;
    data.sOrderID = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID().replace(/-/g, "").toUpperCase() : `${Date.now()}${Math.random()}`.replace(/\D/g, "").toUpperCase();

    console.warn("[DI-33][DB-LAYER-BACKSTOP] Stale sOrderID caught at write time — a call site is missing its own identity check. Regenerated to prevent order overwrite.", {
      staleSOrderID,
      newSOrderID: data.sOrderID,
      documentno: data.documentno,
      overwrittenDocumentno: existingOrder.documentno,
    });

    logAuditEntry?.({
      event: "stale-identity-recovery",
      documentno: data.documentno,
      staleSOrderID,
      newSOrderID: data.sOrderID,
      existingDocumentno: existingOrder.documentno,
      flow: "db-layer-backstop",
    });
  } catch (err) {
    console.warn("[DI-33][DB-LAYER-BACKSTOP] Guard check failed (non-blocking):", err?.message);
  }
}

/**
 * Estimate-level dedup guard. An estimate/online-order retrieval (scan or
 * manual "Retrieve") carries no client-side lock — nothing marks the source
 * estimate consumed, so the same still-"PE" estimate can be retrieved and
 * billed a second time before the first sale's completion (or the server
 * round-trip that would close the estimate) lands. Each retrieval mints its
 * own cart with a fresh documentno preview, so shouldRegenerateIdentityAtSave
 * (scoped to a single sOrderID slot) doesn't catch this — a second retrieval's
 * documentno mismatch just makes it regenerate a brand-new sOrderID instead of
 * blocking, converting a timing gap into a genuine second order.
 *
 * Production incident: KKPPOS/5612 and KKPPOS/5613, same s_estimate_id, two
 * different sOrderIDs, 5 seconds apart.
 *
 * @param {Array} allOrders - result of db.orders.toArray() (caller-supplied so
 *   a call site that already fetched it — e.g. syncOrders' pending-orders scan
 *   — doesn't pay for a second full-table read)
 * @param {string} estimateId
 * @param {string} excludeSOrderID - the current cart's own sOrderID, so a
 *   legitimate retry/resave of the SAME order is never mistaken for a duplicate
 */
export function findDuplicateEstimateOrder(allOrders, estimateId, excludeSOrderID) {
  if (!estimateId) return null;
  return (allOrders || []).find((o) => o.estimateId === estimateId && o.sOrderID !== excludeSOrderID) || null;
}

/**
 * Issue-7 / FK-3692,6319 — snapshot per-order customer-address + B2B state onto
 * a cart record right before it's written to db.orders (checkout, cancel, and
 * discard-parked-bill all write here — same call-site pattern as
 * shouldRegenerateIdentityAtSave above).
 *
 * syncOrders builds the backend orderJson (sCustomerBillingId,
 * sCustomerShippingId, sCustomerId, b2bOrder) from db.orders rows in a batch
 * loop that can process orders long after they were created. It previously
 * read the LIVE localStorage "customerAddressIds" and useTransactionStore
 * isB2bEnabled at THAT later point — correct only for whichever order happens
 * to match whatever customer/session is active at sync time, and silently
 * wrong for every other order sitting in the queue. That leaks one customer's
 * billing/shipping address id (or B2B status) onto an unrelated order — the
 * confirmed mechanism behind the recurring s_customer_billing_id FK violations
 * (tickets 3692, 6319) and unexpected-s_customer_id Pending Sync failures.
 *
 * Capturing here, at write time, is unconditional (not gated behind
 * disableOfflineBilling or any other config) so it protects both the
 * always-online and offline-queued paths alike. `=== undefined` guards make
 * this idempotent — never overwrites a value already set earlier in this
 * cart's lifecycle (e.g. the /estimate-route customerAddressIds already
 * stamped by customer.js), and skipped entirely for already-synced rows
 * (isSynced 1), which don't need correcting.
 *
 * @param {Object} cartToDb - the row about to be written (mutated in place)
 */
export const captureOrderCreationSnapshot = (cartToDb) => {
  if (!cartToDb) return;
  if (cartToDb.isSynced === 1 || cartToDb.isSynced === "1") return;
  if (cartToDb.customer && cartToDb.customer.customerAddressIds === undefined) {
    try {
      const storedAddressIds = JSON.parse(localStorage.getItem("customerAddressIds") || "null");
      if (storedAddressIds) cartToDb.customer.customerAddressIds = storedAddressIds;
    } catch (e) {
      /* leave unset; syncOrders falls back to the legacy live read for this order */
    }
  }
  if (cartToDb.isB2bEnabledSnapshot === undefined) {
    try {
      cartToDb.isB2bEnabledSnapshot = useTransactionStore.getState().isB2bEnabled ?? null;
    } catch (e) {
      /* leave unset; syncOrders falls back to the legacy live read for this order */
    }
  }
};
